import { retryWithDelays } from './retryWithDelays';

const STARTUP_IMPORT_RETRY_DELAYS_MS = [250, 1000, 3000] as const;

// The boot reload budget is shared with index.html's inline startup-recovery
// script (identical key and constants), so every pre-mount recovery path,
// resource failures, the development route watchdog, the module-import
// catch, and this in-bundle recovery, draws from one bounded allowance
// instead of compounding into a reload loop. A successful boot clears it.
const BOOT_RELOAD_ATTEMPTS_KEY = 'rudi_boot_reload_attempts';
export const MAX_BOOT_RELOADS = 10;
// This window exceeds the complete reload and import retry ladder. A shorter
// window could refresh the allowance during one outage and create a loop.
export const BOOT_RELOAD_WINDOW_MS = 600000;
export const BOOT_RELOAD_DELAYS_MS = [
  250, 750, 1500, 3000, 5000, 8000, 10000, 10000, 10000, 10000,
] as const;

type StartupRecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface BootReloadAttempts {
  count: number;
  startedAt: number;
}

export function isDynamicModuleFetchFailure(error: unknown): boolean {
  const message = String(error instanceof Error ? error.message : error).toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

export function isModuleFetchFailure(error: unknown): boolean {
  const message = String(error instanceof Error ? error.message : error).toLowerCase();
  return (
    isDynamicModuleFetchFailure(error) ||
    message.includes('502 bad gateway') ||
    message.includes('503 service unavailable') ||
    message === 'load failed'
  );
}

export async function loadStartupModule<T>(
  loader: () => Promise<T>,
  retryDelays: readonly number[] = STARTUP_IMPORT_RETRY_DELAYS_MS,
  pause?: (delayMs: number) => Promise<void>
): Promise<T> {
  return retryWithDelays(loader, {
    retryDelays,
    pause,
    shouldRetry: isModuleFetchFailure,
  });
}

function readBootReloadAttempts(storage: StartupRecoveryStorage): BootReloadAttempts {
  try {
    const stored: unknown = JSON.parse(storage.getItem(BOOT_RELOAD_ATTEMPTS_KEY) || 'null');
    if (
      stored &&
      typeof stored === 'object' &&
      typeof (stored as BootReloadAttempts).count === 'number' &&
      typeof (stored as BootReloadAttempts).startedAt === 'number'
    ) {
      return stored as BootReloadAttempts;
    }
  } catch {
    // Corrupt state falls through to a fresh allowance.
  }
  return { count: 0, startedAt: 0 };
}

function consumeBootReloadSlot(
  storage: StartupRecoveryStorage,
  reloadDelays: readonly number[]
): number {
  try {
    const now = Date.now();
    const stored = readBootReloadAttempts(storage);
    const withinWindow = now - stored.startedAt <= BOOT_RELOAD_WINDOW_MS && stored.count > 0;
    const attempts = withinWindow ? stored.count : 0;
    if (attempts >= MAX_BOOT_RELOADS) return -1;
    storage.setItem(
      BOOT_RELOAD_ATTEMPTS_KEY,
      JSON.stringify({
        count: attempts + 1,
        startedAt: withinWindow ? stored.startedAt : now,
      })
    );
    return reloadDelays[attempts] ?? 20000;
  } catch {
    // Storage can be unavailable in restricted browser contexts.
    return -1;
  }
}

export function recoverStartupModuleFailure(
  error: unknown,
  storage: StartupRecoveryStorage = window.sessionStorage,
  reload: () => void = () => window.location.reload(),
  reloadDelays: readonly number[] = BOOT_RELOAD_DELAYS_MS
): boolean {
  // Any failure while bootstrapping, whether a module fetch or initialization
  // error, gets a bounded number of full reloads. Transient delivery
  // failures surface with engine-specific messages no classifier can
  // enumerate, and only pre-mount failures reach this path, so a genuine
  // defect still ends on the fallback UI after the budget is spent.
  void error;
  const delay = consumeBootReloadSlot(storage, reloadDelays);
  if (delay < 0) return false;
  window.setTimeout(reload, delay);
  return true;
}

export function markStartupModulesLoaded(
  storage: StartupRecoveryStorage = window.sessionStorage
): void {
  try {
    storage.removeItem(BOOT_RELOAD_ATTEMPTS_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}
